import React from "react";
import { Pie, PieChart, Tooltip } from "recharts";
import {
  formatChartData,
  formatMoney,
  useGetPaymentCurrencyList,
} from "../../utils";
import { useCompanyStore } from "../../state/useCompanyStore";

export type ChartInput = {
  name: string;
  value: number;
  amount: number;
};

export type ChartData = {
  name: string;
  value: number | string;
  amount: number;
  fill: string;
};

export default function SharedPieChart({
  data,
  baseColor = "#F79009",
  expand = false,
}: {
  data?: any;
  baseColor?: string;
  expand?: boolean;
}) {
  const companyProfile = useCompanyStore();
  const { userCurrencyCode } = useGetPaymentCurrencyList(
    companyProfile.company,
  );
  const [chartData, setChartData] = React.useState<ChartData[]>([]);
  const [otherChartData, setOtherChartData] = React.useState<ChartData[]>([]);

  React.useEffect(() => {
    if (data) {
      // @ts-ignore
      setChartData(formatChartData(data, baseColor).chartData);
      setOtherChartData(formatChartData(data, baseColor).allData);
    }
  }, []);

  return (
    <>
      {chartData?.length == 0 ? (
        <div className=" px-3 py-2 ">
          <div className=" relative w-full flex justify-center items-center">
            <span className="h-[200px] bg-[#E6EEF5] inline-block w-[200px] rounded-full"></span>
            <span className="h-[100px] bg-white absolute inline-block w-[100px] rounded-full"></span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row items-center justify-center w-full gap-5">
          <div className="mb:flex-1 md-3 lg:mb-0">
            <PieChart width={220} height={expand ? 250 : 200}>
              <Pie
                data={expand ? otherChartData : chartData}
                dataKey="value"
                nameKey="name"
                innerRadius={expand ? 50 : 40}
                outerRadius={expand ? 105 : 85}
                paddingAngle={0}
                style={{ outline: "none" }}
              />
              <Tooltip
                formatter={(value: number, name: string, entry: any) =>
                  `${formatMoney(entry.payload.amount, userCurrencyCode.currency)}`
                }
                contentStyle={{
                  fontSize: "12px",
                  padding: "5px",
                  backgroundColor: "#FFFFFF",
                  border: "none",
                  borderRadius: "8px",
                }}
              />
            </PieChart>
          </div>
          {expand ? (
            <div className="flex gap-10">
              <div className="grid lg:grid-cols-3 grid-cols- gap-5">
                {otherChartData &&
                  otherChartData.map((item, i) => (
                    <div className="flex flex-row gap-3 items-start" key={i}>
                      <div
                        className="rounded-lg w-[8px] mt-1 h-[8px]"
                        style={{ backgroundColor: item.fill }}
                      ></div>
                      <div>
                        <p className="text-[14px] leading-4 font-normal text-[#667085] ">
                          <span>{item.name}</span>
                        </p>
                        <p className="text-[14px] mt-2 font-semibold leading-6 text-[#344054]">
                          <span>
                            {formatMoney(
                              item.amount,
                              userCurrencyCode.currency,
                            )}{" "}
                            ({item.value}%)
                          </span>
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <div className="mb:flex-1 flex md-3 mb:mb-0 flex-col gap-3 justify-center">
              {chartData &&
                chartData.map((item, i) => (
                  <div className="flex flex-row gap-3 items-center" key={i}>
                    <div
                      className="rounded-lg shrink-0 w-[8px] h-[8px]"
                      style={{ backgroundColor: item.fill }}
                    ></div>
                    <p className="text-[14px] leading-4 font-medium text-[#344054]">
                      <span>{item.name}</span> <span> - {item.value}%</span>
                    </p>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
